import React, { useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDropzone } from 'react-dropzone';
import { steganographyAPI, DecodeResponse } from '../services/api';

interface DecodeTabProps {
  onImageProcessed?: () => void;
}

// Styled components for NEXUS design
const DecodeContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '30px',
}));

const SectionPanel = styled(Box)(({ theme }) => ({
  border: '1px solid #00F0FF',
  backgroundColor: 'rgba(11, 8, 19, 0.6)',
  padding: '25px',
  borderRadius: '5px',
  boxShadow: '0 0 15px rgba(0, 240, 255, 0.1)',
  [theme.breakpoints.down('md')]: {
    padding: '20px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '15px',
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 'bold',
  color: '#00F0FF',
  fontFamily: '"Press Start 2P", cursive',
  marginBottom: '20px',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  textShadow: '0 0 10px rgba(0, 240, 255, 0.5)',
  [theme.breakpoints.down('md')]: {
    fontSize: '12px',
    marginBottom: '15px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '10px',
    marginBottom: '10px',
  },
}));

const DropzoneArea = styled(Box)<{ isdragactive: boolean }>(({ isdragactive, theme }) => ({
  border: isdragactive ? '2px dashed #00F0FF' : '2px dashed #6B6A78',
  borderRadius: '5px',
  padding: '40px',
  textAlign: 'center',
  cursor: 'pointer',
  backgroundColor: isdragactive ? 'rgba(0, 240, 255, 0.1)' : 'transparent',
  minHeight: '200px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.3s ease',
  [theme.breakpoints.down('md')]: {
    padding: '30px',
    minHeight: '150px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '20px',
    minHeight: '120px',
  },
  '&:hover': {
    borderColor: '#00F0FF',
    boxShadow: '0 0 20px rgba(0, 240, 255, 0.2)',
  },
}));

const DropzoneIcon = styled(Box)(({ theme }) => ({
  fontSize: '48px',
  color: '#6B6A78',
  marginBottom: '15px',
  [theme.breakpoints.down('md')]: {
    fontSize: '36px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '28px',
    marginBottom: '10px',
  },
}));

const DropzoneText = styled(Typography)(({ theme }) => ({
  color: '#6B6A78',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '14px',
  [theme.breakpoints.down('md')]: {
    fontSize: '12px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '10px',
  },
}));

const DropzoneSubtext = styled(Typography)(({ theme }) => ({
  color: '#6B6A78',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '11px',
  marginTop: '8px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '9px',
  },
}));

const NexusTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderColor: '#6B6A78',
    color: '#FFFFFF',
    fontFamily: '"JetBrains Mono", monospace',
    '& fieldset': {
      borderColor: '#6B6A78',
    },
    '&:hover fieldset': {
      borderColor: '#00F0FF',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#00F0FF',
      boxShadow: '0 0 10px rgba(0, 240, 255, 0.3)',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#6B6A78',
    fontFamily: '"Press Start 2P", cursive',
    fontSize: '10px',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#00F0FF',
  },
  '& .MuiInputBase-input': {
    color: '#FFFFFF',
  },
}));

const NexusSelect = styled(Select)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderColor: '#6B6A78',
    color: '#FFFFFF',
    fontFamily: '"JetBrains Mono", monospace',
    '& fieldset': {
      borderColor: '#6B6A78',
    },
    '&:hover fieldset': {
      borderColor: '#00F0FF',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#00F0FF',
    },
  },
}));

const NexusButton = styled(Button)<{ nexusVariant?: 'primary' | 'secondary' | 'danger' | string }>(({ nexusVariant = 'primary', theme }) => ({
  border: nexusVariant === 'primary' ? '2px solid #00F0FF' : 
           nexusVariant === 'secondary' ? '2px solid #FFD200' : 
           '2px solid #FF007A',
  color: nexusVariant === 'primary' ? '#00F0FF' : 
         nexusVariant === 'secondary' ? '#FFD200' : 
         '#FF007A',
  padding: '12px 25px',
  fontFamily: '"Press Start 2P", cursive',
  fontSize: '11px',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  backgroundColor: 'transparent',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  [theme.breakpoints.down('md')]: {
    padding: '10px 20px',
    fontSize: '10px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '8px 15px',
    fontSize: '9px',
  },
  '&:hover': {
    borderColor: nexusVariant === 'primary' ? '#00F0FF' : 
             nexusVariant === 'secondary' ? '#FFD200' : 
             '#FF007A',
    color: nexusVariant === 'primary' ? '#00F0FF' : 
          nexusVariant === 'secondary' ? '#FFD200' : 
          '#FF007A',
    boxShadow: `0 0 15px ${nexusVariant === 'primary' ? 'rgba(0, 240, 255, 0.5)' : 
                           nexusVariant === 'secondary' ? 'rgba(255, 210, 0, 0.5)' : 
                           'rgba(255, 0, 122, 0.5)'}`,
    transform: 'translateY(-2px)',
  },
  '&:active': {
    backgroundColor: nexusVariant === 'primary' ? '#00F0FF' : 
                   nexusVariant === 'secondary' ? '#FFD200' : 
                   '#FF007A',
    color: '#0B0813',
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
}));

const StatusMessage = styled(Box)<{ type: 'error' | 'success' }>(({ type, theme }) => ({
  padding: '15px',
  border: `1px solid ${type === 'error' ? '#FF007A' : '#39FF14'}`,
  backgroundColor: type === 'error' ? 'rgba(255, 0, 122, 0.1)' : 'rgba(57, 255, 20, 0.1)',
  borderRadius: '5px',
  color: type === 'error' ? '#FF007A' : '#39FF14',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '13px',
  marginBottom: '20px',
  boxShadow: type === 'error' ? '0 0 15px rgba(255, 0, 122, 0.2)' : '0 0 15px rgba(57, 255, 20, 0.2)',
}));

const ImagePreview = styled(Box)(({ theme }) => ({
  marginTop: '20px',
  padding: '15px',
  border: '1px solid #39FF14',
  backgroundColor: 'rgba(57, 255, 20, 0.05)',
  borderRadius: '5px',
}));

const PreviewImage = styled('img')(({ theme }) => ({
  width: '100%',
  maxHeight: '200px',
  objectFit: 'contain',
  border: '1px solid #39FF14',
}));

const FileInfo = styled(Typography)(({ theme }) => ({
  color: '#39FF14',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '11px',
  marginTop: '10px',
}));

const HelperText = styled(Typography)(({ theme }) => ({
  color: '#6B6A78',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '11px',
  marginTop: '5px',
}));

const DecodedMessagePanel = styled(Box)(({ theme }) => ({
  border: '1px solid #39FF14',
  backgroundColor: 'rgba(57, 255, 20, 0.05)',
  padding: '20px',
  borderRadius: '5px',
  boxShadow: '0 0 15px rgba(57, 255, 20, 0.2)',
}));

const DecodedMessageText = styled(Typography)(({ theme }) => ({
  color: '#FFFFFF',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '14px',
  wordBreak: 'break-word',
  whiteSpace: 'pre-wrap',
}));

const HistoryPanel = styled(Box)(({ theme }) => ({
  border: '1px solid #6B6A78',
  backgroundColor: 'rgba(11, 8, 19, 0.6)',
  padding: '20px',
  borderRadius: '5px',
}));

const HistoryItem = styled(Box)(({ theme }) => ({
  border: '1px solid #6B6A78',
  backgroundColor: 'rgba(11, 8, 19, 0.4)',
  padding: '15px',
  borderRadius: '5px',
  marginBottom: '10px',
}));

const HistoryMessage = styled(Typography)(({ theme }) => ({
  color: '#FFFFFF',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '12px',
  wordBreak: 'break-word',
  marginBottom: '8px',
}));

const HistoryMeta = styled(Typography)(({ theme }) => ({
  color: '#6B6A78',
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '10px',
}));

interface DecodedMessage {
  message: string;
  timestamp: string;
  filename: string;
}

const DecodeTab: React.FC<DecodeTabProps> = ({ onImageProcessed }: DecodeTabProps) => {
  const [image, setImage] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [bitDepth, setBitDepth] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [decodedMessage, setDecodedMessage] = useState<DecodedMessage | null>(null);
  const [decodedHistory, setDecodedHistory] = useState<DecodedMessage[]>([]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': ['.png', '.bmp', '.jpg', '.jpeg']
    },
    maxFiles: 1,
    maxSize: 50 * 1024 * 1024, // 50MB
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        setImage(file);
        const url = URL.createObjectURL(file);
        setImageUrl(url);
        setDecodedMessage(null);
        setError('');
      }
    }
  });

  const handleDecode = async () => {
    // Image validation
    if (!image) {
      setError('Please select an image');
      return;
    }

    // Validate image type
    const validImageTypes = ['image/png', 'image/bmp', 'image/jpeg', 'image/jpg'];
    if (!validImageTypes.includes(image.type)) {
      setError('Invalid image type. Please use PNG, BMP, or JPEG.');
      return;
    }

    // Validate image size (max 50MB)
    const maxSize = 50 * 1024 * 1024;
    if (image.size > maxSize) {
      setError('Image size exceeds 50MB limit.');
      return;
    }

    // Password validation
    if (!password) {
      setError('Please enter the password');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    if (password.length > 128) {
      setError('Password is too long. Maximum 128 characters allowed.');
      return;
    }

    // Bit depth validation
    if (![1, 2, 4].includes(bitDepth)) {
      setError('Invalid bit depth. Must be 1, 2, or 4.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response: DecodeResponse = await steganographyAPI.decode(
        image,
        password,
        bitDepth
      );

      if (response.success && response.message) {
        const newDecoded: DecodedMessage = {
          message: response.message,
          timestamp: new Date().toISOString(),
          filename: image.name
        };

        setDecodedMessage(newDecoded);
        setDecodedHistory(prev => [newDecoded, ...prev.slice(0, 9)]); // Keep last 10
        onImageProcessed?.();
      } else {
        setError(response.error || 'No hidden message found or incorrect password');
      }
    } catch (err: any) {
      setError(err.message || 'Decoding failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyMessage = (message: string) => {
    navigator.clipboard.writeText(message);
  };

  const handleDownloadMessage = (message: string, filename: string) => {
    const blob = new Blob([message], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `decoded_message_${filename.replace(/\.[^/.]+$/, '')}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleClear = () => {
    setImage(null);
    setImageUrl('');
    setPassword('');
    setDecodedMessage(null);
    setError('');
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <DecodeContainer>
      <Grid container spacing={{ xs: 2, md: 3 }}>
        {/* Image Upload Section */}
        <Grid item xs={12} md={6}>
          <SectionPanel>
            <SectionTitle>01 / ENCODED WORLD</SectionTitle>
            
            <DropzoneArea {...getRootProps()} isdragactive={isDragActive}>
              <input {...getInputProps()} />
              <DropzoneIcon>📁</DropzoneIcon>
              <DropzoneText>
                {isDragActive ? 'DROP IMAGE HERE' : 'DRAG & DROP IMAGE OR CLICK TO SELECT'}
              </DropzoneText>
              <DropzoneSubtext>
                PNG, BMP, JPEG (MAX 50MB)
              </DropzoneSubtext>
            </DropzoneArea>

            {imageUrl && (
              <ImagePreview>
                <PreviewImage
                  src={imageUrl}
                  alt="Encoded"
                />
                <FileInfo>
                  // FILE: {image?.name} ({(image?.size || 0) / 1024} KB)
                </FileInfo>
              </ImagePreview>
            )}
          </SectionPanel>
        </Grid>

        {/* Controls Section */}
        <Grid item xs={12} md={6}>
          <SectionPanel>
            <SectionTitle>02 / DECRYPTION PARAMETERS</SectionTitle>

            <NexusTextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label="DECRYPTION KEY"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
            />

            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              <NexusButton
                nexusVariant="secondary"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? 'HIDE KEY' : 'SHOW KEY'}
              </NexusButton>
            </Box>

            <FormControl fullWidth margin="normal">
              <InputLabel style={{ color: '#6B6A78', fontFamily: '"Press Start 2P", cursive', fontSize: '10px' }}>BIT DEPTH</InputLabel>
              <NexusSelect
                value={bitDepth}
                onChange={(e) => setBitDepth(Number(e.target.value))}
                label="BIT DEPTH"
              >
                <MenuItem value={1}>1-BIT</MenuItem>
                <MenuItem value={2}>2-BIT</MenuItem>
                <MenuItem value={4}>4-BIT</MenuItem>
              </NexusSelect>
            </FormControl>

            <HelperText>
              // USE THE SAME BIT DEPTH THAT WAS USED FOR ENCODING
            </HelperText>

            <Box sx={{ mt: 3, display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <NexusButton
                nexusVariant="primary"
                onClick={handleDecode}
                disabled={loading || !image || !password}
                fullWidth
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : '🔓 DECODE MESSAGE'}
              </NexusButton>
              <NexusButton
                nexusVariant="danger"
                onClick={handleClear}
                fullWidth
              >
                CLEAR
              </NexusButton>
            </Box>
          </SectionPanel>
        </Grid>

        {/* Error Display */}
        {error && (
          <Grid item xs={12}>
            <StatusMessage type="error">
              // ERROR: {error}
            </StatusMessage>
          </Grid>
        )}

        {/* Decoded Message Display */}
        {decodedMessage && (
          <Grid item xs={12}>
            <DecodedMessagePanel>
              <SectionTitle>03 / DECODED MESSAGE</SectionTitle>
              <DecodedMessageText>
                {decodedMessage.message}
              </DecodedMessageText>
              <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                <Tooltip title="Copy to clipboard">
                  <IconButton 
                    onClick={() => handleCopyMessage(decodedMessage.message)}
                    sx={{ color: '#00F0FF' }}
                  >
                    📋
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download as text file">
                  <IconButton 
                    onClick={() => handleDownloadMessage(decodedMessage.message, decodedMessage.filename)}
                    sx={{ color: '#00F0FF' }}
                  >
                    📥
                  </IconButton>
                </Tooltip>
                <HelperText sx={{ ml: 'auto' }}>
                  // {decodedMessage.filename} • {formatTimestamp(decodedMessage.timestamp)}
                </HelperText>
              </Box>
            </DecodedMessagePanel>
          </Grid>
        )}

        {/* Decoding History */}
        {decodedHistory.length > 0 && (
          <Grid item xs={12}>
            <HistoryPanel>
              <SectionTitle>RECENT DECODINGS</SectionTitle>
              {decodedHistory.map((decoded, index) => (
                <HistoryItem key={index}>
                  <HistoryMessage>
                    {decoded.message.length > 100 
                      ? decoded.message.substring(0, 100) + '...' 
                      : decoded.message}
                  </HistoryMessage>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1 }}>
                    <Tooltip title="Copy to clipboard">
                      <IconButton 
                        size="small"
                        onClick={() => handleCopyMessage(decoded.message)}
                        sx={{ color: '#6B6A78' }}
                      >
                        📋
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download as text file">
                      <IconButton 
                        size="small"
                        onClick={() => handleDownloadMessage(decoded.message, decoded.filename)}
                        sx={{ color: '#6B6A78' }}
                      >
                        📥
                      </IconButton>
                    </Tooltip>
                    <HistoryMeta sx={{ ml: 'auto' }}>
                      // {decoded.filename} • {formatTimestamp(decoded.timestamp)}
                    </HistoryMeta>
                  </Box>
                </HistoryItem>
              ))}
            </HistoryPanel>
          </Grid>
        )}
      </Grid>
    </DecodeContainer>
  );
};

export default DecodeTab;
